import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Typography, Divider, Button, Row, Col } from 'antd';
import { CrownOutlined, DownloadOutlined, CopyOutlined, ArrowLeftOutlined, CheckOutlined, TrophyOutlined } from '@ant-design/icons';
import { certificateService } from 'PlatForms/SelfLMS/Services/Certificate/CertificateService';
import { toast } from 'react-toastify';
import './Certificate.css';
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import badgeImage from "Common/hooks/assets/images/badge.jpg";
import signature1 from "Common/hooks/assets/images/signature1.jpg";
import signature2 from "Common/hooks/assets/images/signature2.jpg";

const { Title, Text } = Typography;

const CertificateDetailPage = () => {
  const { certificateId } = useParams();
  const navigate = useNavigate();
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);
  const certificateRef = useRef(null);

  useEffect(() => {
    const fetchCertificate = async () => {
      try {
        setLoading(true);
        const data = await certificateService.getCertificateById(certificateId);
        setCertificate(data);
      } catch (error) {
        console.error(error);
        toast.error('Failed to load certificate details');
      } finally {
        setLoading(false);
      }
    };

    fetchCertificate();
  }, [certificateId]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleDownloadPDF = async () => {
    if (!certificate) {
      toast.error('Certificate data not loaded');
      return;
    }

    try {
      setDownloading(true);
      toast.info('Generating PDF...');

      const certificateElement = document.getElementById("badge-container");
      
      if (!certificateElement) {
        toast.error('Certificate element not found');
        return;
      }

      const canvas = await html2canvas(certificateElement, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL("image/png");
      // The badge card is landscape-shaped (wider than tall), so render it on a
      // landscape A4 and fit it (aspect preserved, centered). A portrait page
      // left large empty margins above and below the card.
      const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgProps = pdf.getImageProperties(imgData);
      const ratio = Math.min(pageWidth / imgProps.width, pageHeight / imgProps.height);
      const drawWidth = imgProps.width * ratio;
      const drawHeight = imgProps.height * ratio;
      const offsetX = (pageWidth - drawWidth) / 2;
      const offsetY = (pageHeight - drawHeight) / 2;

      pdf.addImage(imgData, "PNG", offsetX, offsetY, drawWidth, drawHeight);
      const fileName = `${(certificate.title || "Certificate").replace(/\s+/g, "_")}_Badge.pdf`;
      pdf.save(fileName);

      toast.success('Badge PDF downloaded successfully!');

    } catch (err) {
      console.error("PDF download error:", err);
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!certificate) return;

    const link = `${window.location.origin}/credential-verify?sharingId=${certificate.sharingId}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Badge link copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy link");
    }
  };

  if (loading) {
    return (
      <div className="certificate-loading">
        <div className="loading-spinner"></div>
        <Text>Loading badge...</Text>
      </div>
    );
  }

  if (!certificate) {
    return (
      <div className="certificate-error">
        <Title level={3}>Badge not found</Title>
        <Button type="primary" onClick={() => navigate(-1)}>
          <ArrowLeftOutlined /> Back
        </Button>
      </div>
    );
  }

  if (!certificate.learner) {
    return (
      <div className="certificate-error">
        <Title level={3}>Invalid Badge Data</Title>
        <Button type="primary" onClick={() => navigate(-1)}>
          <ArrowLeftOutlined /> Back
        </Button>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "20px",
        background: "linear-gradient(135deg, #FFFDF8 0%, #F5EFE2 100%)",
        minHeight: "100vh",
      }}
    >
      {/* Action Buttons - More Compact */}
      <div
        style={{
          width: "100%",
          maxWidth: "800px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <Button
          type="default"
          onClick={() => navigate(-1)}
          icon={<ArrowLeftOutlined />}
          style={{ padding: "8px 16px" }}
        >
          Back
        </Button>

        <div style={{ display: "flex", gap: "10px" }}>
          <Button
            type="primary"
            onClick={handleDownloadPDF}
            loading={downloading}
            disabled={downloading}
            icon={<DownloadOutlined />}
            style={{ padding: "8px 16px", background: "#9A6B0E", borderColor: "#9A6B0E" }}
          >
            {downloading ? 'Generating...' : 'Download'}
          </Button>

          <Button
            type="default"
            icon={copied ? <CheckOutlined /> : <CopyOutlined />}
            onClick={handleCopyLink}
            style={{ padding: "8px 16px" }}
          >
            {copied ? "Copied!" : "Copy Link"}
          </Button>
        </div>
      </div>

      {/* Badge Container - No Border */}
      <div
        id="badge-container"
        style={{
          position: "relative",
          width: "100%",
          maxWidth: "800px",
          background: "white",
          boxShadow: "0 8px 25px rgba(0, 0, 0, 0.1)",
          overflow: "hidden",
          margin: "0 auto",
        }}
      >
        {/* LMS Logo at Left Corner */}
        <div
          style={{
            position: "absolute",
            top: "15px",
            left: "15px",
            zIndex: 10,
          }}
        >
          <img
            src={`${process.env.PUBLIC_URL}/logo-icon.png`}
            alt="CoreIQPro"
            style={{
              width: "50px",
              height: "50px",
              objectFit: "contain",
            }}
          />
        </div>

        {/* Badge Content - Compact Layout */}
        <div
          style={{
            padding: "40px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          {/* Badge Image - Larger */}
          <div
            style={{
              width: "280px",
              height: "280px",
              margin: "0 auto 15px auto", // Reduced from 20px to 15px
              position: "relative",
            }}
          >
            <img
              src={badgeImage}
              alt="Achievement Badge"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: "8px",
              }}
            />
          </div>

          {/* Learner Name - Smaller Font with reduced top margin */}
          <Title
            level={2}
            style={{
              margin: "5px 0 5px 0", // Reduced top margin from 10px to 5px
              fontWeight: "bold",
              color: "#9A6B0E",
              fontSize: "28px",
            }}
          >
            {certificate.learner.firstName} {certificate.learner.lastName}
          </Title>

          {/* Simple Text - Compact */}
          <Text
            style={{
              fontSize: "16px",
              color: "#444",
              marginBottom: "5px", // Reduced from 8px to 5px
            }}
          >
            has successfully completed
          </Text>

          {/* Course Title - Simple Text */}
          <Text
            strong
            style={{
              fontSize: "22px",
              color: "#9A6B0E",
              marginBottom: "5px", // Reduced from 8px to 5px
            }}
          >
            {certificate.title}
          </Text>

          {/* Date - Simple Text */}
          <Text
            style={{
              fontSize: "14px",
              color: "#555",
              marginBottom: "25px", // Reduced from 30px to 25px
            }}
          >
            Completed on {formatDate(certificate.issuedOn)}
          </Text>

          {/* Signatures - Compact */}
          <div
            style={{
              width: "100%",
              paddingTop: "15px", // Reduced from 20px to 15px
              borderTop: "1px solid #E0E0E0",
            }}
          >
            <Row justify="center" gutter={[30, 10]}>
              <Col span={10} style={{ textAlign: "center" }}>
                <img
                  src={signature1}
                  alt="Signature 1"
                  style={{
                    width: "120px",
                    height: "auto",
                    marginBottom: "5px",
                    objectFit: "contain",
                  }}
                />
                <div style={{ marginTop: "5px" }}> {/* Reduced from 8px to 5px */}
                  <Text style={{ fontSize: "14px", color: "#333" }}>
                    Program Director
                  </Text>
                </div>
              </Col>
              
              <Col span={10} style={{ textAlign: "center" }}>
                <img
                  src={signature2}
                  alt="Signature 2"
                  style={{
                    width: "120px",
                    height: "auto",
                    marginBottom: "5px",
                    objectFit: "contain",
                  }}
                />
                <div style={{ marginTop: "5px" }}> {/* Reduced from 8px to 5px */}
                  <Text style={{ fontSize: "14px", color: "#333" }}>
                    Institution Head
                  </Text>
                </div>
              </Col>
            </Row>
          </div>
        </div>
      </div>

    </div>
  );
};

export default CertificateDetailPage;